// Thin client over the /api/v1/aop/* endpoints, plus shared formatting
// helpers for the AOP dashboard views. Auth reuses the app's JWT token via
// AuthHelperMethods.
import AuthHelperMethods from '../auth/AuthHelperMethods';

const Auth = new AuthHelperMethods();

const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'all');
  if (!entries.length) return '';
  return '?' + new URLSearchParams(entries).toString();
};

const get = (url) => Auth.fetch(url, { method: 'GET' });
const post = (url, body) => Auth.fetch(url, { method: 'POST', body: JSON.stringify(body || {}) });

export const aopApi = {
  status: () => get('/api/v1/aop/status'),
  runs: (params) => get('/api/v1/aop/runs' + qs(params)),
  run: (id) => get(`/api/v1/aop/runs/${id}`),
  audit: (params) => get('/api/v1/aop/audit' + qs(params)),
  reports: (params) => get('/api/v1/aop/reports' + qs(params)),
  pendingActions: (params) => get('/api/v1/aop/pending-actions' + qs(params)),
  approve: (id, note) => post(`/api/v1/aop/pending-actions/${id}/approve`, { note }),
  reject: (id, note) => post(`/api/v1/aop/pending-actions/${id}/reject`, { note }),
  requestChanges: (id, note) => post(`/api/v1/aop/pending-actions/${id}/request-changes`, { note }),
  editPendingAction: (id, fields) => post(`/api/v1/aop/pending-actions/${id}/edit`, fields),
  enqueue: (workflow, entityRefs) => post(`/api/v1/aop/workflows/${workflow}/enqueue`, { entityRefs }),
  agents: () => get('/api/v1/aop/agents'),
  config: () => get('/api/v1/aop/config'),
};

// Bridge to the internal CSD MCP server (server/controllers/mcp_bridge.js).
export const mcpApi = {
  status: () => get('/api/v1/mcp/status'),
  tools: () => get('/api/v1/mcp/tools'),
  call: (tool, args) => post('/api/v1/mcp/call', { tool, args }),
};

// ── registry mirrors (keep in sync with server/config/aop_registry.js) ──────
// Code identifiers are role-based; the fun names are display-only labels.
// Both AOP and COP agents/workflows live here so the AOP dashboard can monitor
// and report on COP runs alongside its own. Each entry is tagged by `platform`.
export const PLATFORM_LABELS = { aop: 'AOP', cop: 'COP' };

export const AGENT_LABELS = {
  // AOP operations agents
  orchestrator_bot: 'ChrisGPT',
  copy_bot: 'Samaintha',
  design_bot: 'Raimond',
  dev_bot: 'Devain',
  pm_bot: 'Baitty',
  analytics_bot: 'Ainna',
  process_bot: 'Braiks',
  // COP campaign-optimization agents (plain product names, no ML jargon)
  segment_bot: 'Segment Intelligence',
  opportunity_bot: 'Opportunity Scout',
  campaign_draft_bot: 'Campaign Draft',
  experiment_bot: 'Experiment Monitor',
  reporting_bot: 'Intelligence Reporting',
  research_bot: 'Deep Research',
};

// Agents owned by the COP product surface. copy_bot/design_bot are shared with
// AOP (creative generation) and stay tagged 'aop'.
export const COP_AGENT_IDS = new Set([
  'segment_bot',
  'opportunity_bot',
  'campaign_draft_bot',
  'experiment_bot',
  'reporting_bot',
  'research_bot',
]);

export const WORKFLOWS = [
  // AOP operations workflows
  { id: 'orchestrator-daily-brief', label: 'ChrisGPT Daily Brief', agent: 'orchestrator_bot', needsEntity: false, platform: 'aop' },
  { id: 'orchestrator-weekly-brief', label: 'ChrisGPT Weekly Brief', agent: 'orchestrator_bot', needsEntity: false, platform: 'aop' },
  { id: 'orchestrator-opp-check', label: 'ChrisGPT Hourly Opp Check', agent: 'orchestrator_bot', needsEntity: false, platform: 'aop' },
  { id: 'copy-package', label: 'Samaintha Copy Package', agent: 'copy_bot', needsEntity: true, platform: 'aop' },
  { id: 'design-packet', label: 'Raimond Design Packet', agent: 'design_bot', needsEntity: true, platform: 'aop' },
  { id: 'dev-packet', label: 'Devain Development Packet', agent: 'dev_bot', needsEntity: true, platform: 'aop' },
  { id: 'pm-queue-monitor', label: 'Baitty Queue Monitor', agent: 'pm_bot', needsEntity: false, platform: 'aop' },
  { id: 'pm-opp-shepherd', label: 'Baitty Opportunity Shepherd', agent: 'pm_bot', needsEntity: false, platform: 'aop' },
  { id: 'analytics-report', label: 'Ainna Analytics Report', agent: 'analytics_bot', needsEntity: false, platform: 'aop' },
  { id: 'process-review', label: 'Braiks Process Review', agent: 'process_bot', needsEntity: false, platform: 'aop' },
  // COP campaign-optimization workflows
  { id: 'cop-segment-recluster', label: 'Segment Re-clustering', agent: 'segment_bot', needsEntity: false, platform: 'cop' },
  { id: 'cop-opportunity-scan', label: 'Opportunity Scan', agent: 'opportunity_bot', needsEntity: false, platform: 'cop' },
  { id: 'cop-campaign-draft', label: 'Campaign Draft', agent: 'campaign_draft_bot', needsEntity: true, platform: 'cop' },
  { id: 'cop-experiment-monitor', label: 'Experiment Monitor', agent: 'experiment_bot', needsEntity: false, platform: 'cop' },
  { id: 'cop-winner-declaration', label: 'Winner Declaration', agent: 'experiment_bot', needsEntity: true, platform: 'cop' },
  { id: 'cop-intelligence-report', label: 'Intelligence Report', agent: 'reporting_bot', needsEntity: false, platform: 'cop' },
  { id: 'cop-deep-research', label: 'Deep Research', agent: 'research_bot', needsEntity: true, platform: 'cop' },
];

export const agentForWorkflow = (name) =>
  (WORKFLOWS.find((w) => w.id === name) || {}).agent || String(name || '').split('-')[0];

export const workflowLabel = (name) => (WORKFLOWS.find((w) => w.id === name) || {}).label || name;

export const agentLabel = (agent) => AGENT_LABELS[agent] || agent || 'system';

// Platform of a workflow/agent, derived from the registry (no per-record column
// needed). A run on a `cop-*` workflow or a COP-owned agent is COP; else AOP.
export const platformForWorkflow = (name) => {
  const wf = WORKFLOWS.find((w) => w.id === name);
  if (wf) return wf.platform;
  return String(name || '').startsWith('cop-') ? 'cop' : 'aop';
};

export const platformForAgent = (agent) => (COP_AGENT_IDS.has(agent) ? 'cop' : 'aop');

export const platformLabel = (platform) => PLATFORM_LABELS[platform] || platform;

// 'update_task_comment' -> 'Update Task Comment'
export const prettyActionType = (actionType) =>
  String(actionType || '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// ── time/format helpers ─────────────────────────────────────────────────────
export const formatWhen = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  const mins = Math.round((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
};

export const runDuration = (run) => {
  if (!run || !run.startedAt) return '—';
  const end = run.completedAt ? new Date(run.completedAt) : new Date();
  const secs = Math.max(0, Math.round((end.getTime() - new Date(run.startedAt).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

export const formatTokens = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(v);
};
